import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiError } from "./api";
import { t } from "./i18n";
import { notifyError } from "./toast";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Title of the error toast; the body is always the backend's `detail`. */
      errorTitle?: string;
      /** The caller shows the error itself (inline): no toast. */
      silent?: boolean;
    };
  }
}

/** Client errors that a retry cannot fix. */
function retryable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      // A first load failing shows its own inline error state; a background refetch failing
      // over data already on screen would otherwise go unnoticed.
      onError: (error, query) => {
        if (query.state.data !== undefined) notifyError(error, t("Could not refresh"));
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.silent) return;
        notifyError(error, mutation.meta?.errorTitle);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => retryable(error) && failureCount < 2,
      },
      mutations: { retry: false },
    },
  });
}
